import React, { useState, useContext, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';
import dayjs from 'dayjs';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';
import DoiRateOverTimeChart from '../../components/DoiRateOverTimeChartOptinEmail';

// Interfaces for API data
interface DoiRateByOptInEmailData {
  opt_in_email_id: number;
  name: string;
  subject: string;
  sender_address: string;
  doi_rate: number | null;
  total_distinct_emails: number;
  confirmed_distinct_emails: number;
}

interface DoiRateByEmailDomainData {
  email_domain: string;
  doi_rate: number | null;
  total_distinct_emails: number;
  confirmed_distinct_emails: number;
  email_percentage: number | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`doi-optin-tabpanel-${index}`}
      aria-labelledby={`doi-optin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `doi-optin-tab-${index}`,
    'aria-controls': `doi-optin-tabpanel-${index}`,
  };
}

const DoiEvaluationOptinEmailsPage = () => {
  const { filterState } = useContext(FilterContext)!;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [tabValue, setTabValue] = useState(0);
  const [ratesByOptInEmail, setRatesByOptInEmail] = useState<DoiRateByOptInEmailData[]>([]);
  const [availableOptInEmails, setAvailableOptInEmails] = useState<DoiRateByOptInEmailData[]>([]);
  const [selectedOptInEmailForFilter, setSelectedOptInEmailForFilter] = useState<string>('all');
  const [selectedOptInEmailForTable, setSelectedOptInEmailForTable] =
    useState<DoiRateByOptInEmailData | null>(null);
  const [selectedEmailDomain, setSelectedEmailDomain] = useState<string | null>(null);
  const [abTestGmailFilter, setAbTestGmailFilter] = useState<string>('all');

  const optInEmailHeaderMapping: Record<string, HeaderMappingItem> = {
    name: { name: 'Name', type: 'string' },
    subject: { name: 'Betreff', type: 'string' },
    sender_address: { name: 'Absender', type: 'string' },
    doi_rate: { name: 'DOI Rate', type: 'percentage', digits: 2 },
    total_distinct_emails: { name: 'Total Emails', type: 'number' },
    confirmed_distinct_emails: { name: 'Confirmed Emails', type: 'number' },
  };

  const emailDomainHeaderMapping: Record<string, HeaderMappingItem> = {
    email_domain: { name: 'Email Domain', type: 'string' },
    doi_rate: { name: 'DOI Rate', type: 'percentage', digits: 2 },
    email_percentage: { name: '% of All Emails', type: 'percentage', digits: 2 },
    total_distinct_emails: { name: 'Total Emails', type: 'number' },
    confirmed_distinct_emails: { name: 'Confirmed Emails', type: 'number' },
  };

  // Compute summary for opt-in email rates
  const computeOptInEmailSummary = (data: DoiRateByOptInEmailData[]): DoiRateByOptInEmailData => {
    if (!data.length) {
      return {
        opt_in_email_id: 0,
        name: 'Insgesamt',
        subject: '',
        sender_address: '',
        doi_rate: 0,
        total_distinct_emails: 0,
        confirmed_distinct_emails: 0,
      };
    }

    const summary: DoiRateByOptInEmailData = {
      opt_in_email_id: 0,
      name: 'Insgesamt',
      subject: '',
      sender_address: '',
      doi_rate: 0,
      total_distinct_emails: 0,
      confirmed_distinct_emails: 0,
    };

    // Sum up the totals
    data.forEach((item) => {
      summary.total_distinct_emails += item.total_distinct_emails || 0;
      summary.confirmed_distinct_emails += item.confirmed_distinct_emails || 0;
    });

    // Calculate the overall DOI rate from the totals
    summary.doi_rate =
      summary.total_distinct_emails > 0
        ? summary.confirmed_distinct_emails / summary.total_distinct_emails
        : 0;

    return summary;
  };

  // Compute summary for email domain rates
  const computeEmailDomainSummary = (
    data: DoiRateByEmailDomainData[],
  ): DoiRateByEmailDomainData => {
    if (!data.length) {
      return {
        email_domain: 'Insgesamt',
        doi_rate: 0,
        total_distinct_emails: 0,
        confirmed_distinct_emails: 0,
        email_percentage: 1, // 100% of total
      };
    }

    const summary: DoiRateByEmailDomainData = {
      email_domain: 'Insgesamt',
      doi_rate: 0,
      total_distinct_emails: 0,
      confirmed_distinct_emails: 0,
      email_percentage: 1, // Will always be 100% of total
    };

    // Sum the totals
    data.forEach((item) => {
      summary.total_distinct_emails += item.total_distinct_emails;
      summary.confirmed_distinct_emails += item.confirmed_distinct_emails;
    });

    // Calculate the overall DOI rate
    summary.doi_rate =
      summary.total_distinct_emails > 0
        ? summary.confirmed_distinct_emails / summary.total_distinct_emails
        : 0;

    return summary;
  };

  // Fetch DOI rates by opt-in email
  const fetchOptInEmailRates = useCallback(
    async (
      currentFilterState: IFilterState,
      currentAbTestFilter?: string,
    ): Promise<DoiRateByOptInEmailData[]> => {
      if (!currentFilterState.startDate) return [];
      try {
        const formattedStartDate = dayjs(currentFilterState.startDate).format('YYYY-MM-DD');
        const params: any = { startDate: formattedStartDate };

        if (currentFilterState.endDate) {
          params.endDate = dayjs(currentFilterState.endDate).format('YYYY-MM-DD');
        }

        if (currentAbTestFilter && currentAbTestFilter !== 'all') {
          params.abTestGmail = currentAbTestFilter;
        }

        const response = await fetchData(
          `/api/doi-evaluation-optin-emails/rates-by-optin-email`,
          params,
        );

        // Handle response safely
        let resultArray: DoiRateByOptInEmailData[] = [];

        if (Array.isArray(response)) {
          // Handle both flat arrays and nested arrays
          if (response.length > 0 && Array.isArray(response[0])) {
            // Handle nested arrays by flattening
            const flattenedArray = (response as any[]).flat();
            resultArray = flattenedArray.filter(
              (item) => item && typeof item === 'object' && 'opt_in_email_id' in item,
            ) as DoiRateByOptInEmailData[];
          } else {
            // Handle normal arrays with type checking
            resultArray = response.filter(
              (item) => item && typeof item === 'object' && 'opt_in_email_id' in item,
            ) as DoiRateByOptInEmailData[];
          }
        }

        setRatesByOptInEmail(resultArray);

        // Populate available opt-in emails for the filter dropdown
        setAvailableOptInEmails(resultArray);

        return resultArray;
      } catch (error) {
        console.error('Error fetching DOI rates by opt-in email:', error);
        setRatesByOptInEmail([]);
        setAvailableOptInEmails([]);
        return [];
      }
    },
    [],
  );

  // Fetch DOI rates by email_domain
  const fetchEmailDomainRates = useCallback(
    async (
      currentFilterState: IFilterState,
      currentAbTestFilter?: string,
    ): Promise<DoiRateByEmailDomainData[]> => {
      if (!selectedOptInEmailForTable || !currentFilterState.startDate) {
        return [];
      }

      try {
        const formattedStartDate = dayjs(currentFilterState.startDate).format('YYYY-MM-DD');
        const params: any = {
          optInEmailId: selectedOptInEmailForTable.opt_in_email_id,
          startDate: formattedStartDate,
        };

        if (currentFilterState.endDate) {
          params.endDate = dayjs(currentFilterState.endDate).format('YYYY-MM-DD');
        }

        if (currentAbTestFilter && currentAbTestFilter !== 'all') {
          params.abTestGmail = currentAbTestFilter;
        }

        const response = await fetchData(
          `/api/doi-evaluation-optin-emails/rates-by-email-domain`,
          params,
        );

        // Handle response safely
        let resultArray: DoiRateByEmailDomainData[] = [];

        if (Array.isArray(response)) {
          // Handle both flat arrays and nested arrays
          if (response.length > 0 && Array.isArray(response[0])) {
            // Handle nested arrays by flattening
            const flattenedArray = (response as any[]).flat();
            resultArray = flattenedArray.filter(
              (item) => item && typeof item === 'object' && 'email_domain' in item,
            ) as DoiRateByEmailDomainData[];
          } else {
            // Handle normal arrays with type checking
            resultArray = response.filter(
              (item) => item && typeof item === 'object' && 'email_domain' in item,
            ) as DoiRateByEmailDomainData[];
          }
        }

        return resultArray;
      } catch (error) {
        console.error('Error fetching DOI rates by email domain for opt-in email:', error);
        return [];
      }
    },
    [selectedOptInEmailForTable, abTestGmailFilter],
  );

  const handleOptInEmailFilterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as string;
    setSelectedOptInEmailForFilter(value);

    // If "all" is not selected, also set it for the table to directly load it
    if (value !== 'all') {
      const selectedEmail = availableOptInEmails.find(
        (email) => email.opt_in_email_id.toString() === value,
      );
      setSelectedOptInEmailForTable(selectedEmail || null);
    } else {
      setSelectedOptInEmailForTable(null); // Clear second table if "all" is selected
    }
  };

  const handleAbTestGmailFilterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as string;
    setAbTestGmailFilter(value);
  };

  const handleOptInEmailRowClick = (rowData: DoiRateByOptInEmailData) => {
    if (rowData && rowData.opt_in_email_id) {
      setSelectedOptInEmailForTable(rowData);
      setSelectedOptInEmailForFilter(rowData.opt_in_email_id.toString()); // Sync dropdown
    }
  };

  const handleEmailDomainRowClick = (rowData: DoiRateByEmailDomainData) => {
    if (rowData && rowData.email_domain) {
      setSelectedEmailDomain(rowData.email_domain);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Load data on component mount and when filter changes
  useEffect(() => {
    fetchOptInEmailRates(filterState, abTestGmailFilter);
  }, [filterState.startDate, filterState.endDate, abTestGmailFilter, fetchOptInEmailRates]);

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight="bold" mb="20px">
        Auswertung DOI-Raten nach Opt-in Emails
      </Typography>

      <Paper sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel id="optin-email-filter-label">
                Opt-in Email (für Email Domain Tabelle)
              </InputLabel>
              <Select
                labelId="optin-email-filter-label"
                value={selectedOptInEmailForFilter}
                label="Opt-in Email (für Email Domain Tabelle)"
                onChange={handleOptInEmailFilterChange}
              >
                <MenuItem value="all">Alle Emails (Tabelle unten manuell auswählen)</MenuItem>
                {availableOptInEmails.map((email) => (
                  <MenuItem key={email.opt_in_email_id} value={email.opt_in_email_id.toString()}>
                    {email.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel id="ab-test-gmail-filter-label">AB Test Gmail Filter</InputLabel>
              <Select
                labelId="ab-test-gmail-filter-label"
                value={abTestGmailFilter}
                label="AB Test Gmail Filter"
                onChange={handleAbTestGmailFilterChange}
              >
                <MenuItem value="all">Alle (kein Filter)</MenuItem>
                <MenuItem value="unset">AB_TEST_GMAIL unset</MenuItem>
                <MenuItem value="1">AB_TEST_GMAIL = 1</MenuItem>
                <MenuItem value="0">AB_TEST_GMAIL = 0</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="DOI rate tabs">
          <Tab label="Tabellen" {...a11yProps(0)} />
          <Tab label="Zeitverlauf" {...a11yProps(1)} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3} direction="column">
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: '10px', backgroundColor: colors.primary[400] }}>
              <Typography variant="h6" mb={2} fontWeight="bold">
                DOI-Raten nach Opt-in Email
              </Typography>
              <TableWidget<DoiRateByOptInEmailData>
                key={`optin-email-table-${abTestGmailFilter}`}
                getData={() => fetchOptInEmailRates(filterState, abTestGmailFilter)}
                computeSummary={computeOptInEmailSummary}
                detailsHeaderMapping={optInEmailHeaderMapping}
                initialSortColumn="total_distinct_emails"
                sortOrder="desc"
                excelDownloadName="doi_rates_by_optin_email"
                onRowClick={handleOptInEmailRowClick}
              />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            {selectedOptInEmailForTable ? (
              <Paper sx={{ p: 2, borderRadius: '10px', backgroundColor: colors.primary[400] }}>
                <Typography variant="h6" mb={2} fontWeight="bold">
                  DOI-Raten nach Email-Domain für &quot;{selectedOptInEmailForTable.name}&quot;
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Betreff: {selectedOptInEmailForTable.subject} | Absender:{' '}
                  {selectedOptInEmailForTable.sender_address}
                </Typography>
                <TableWidget<DoiRateByEmailDomainData>
                  key={`email-domain-table-${selectedOptInEmailForTable.opt_in_email_id}-${abTestGmailFilter}`}
                  getData={() => fetchEmailDomainRates(filterState, abTestGmailFilter)}
                  computeSummary={computeEmailDomainSummary}
                  detailsHeaderMapping={emailDomainHeaderMapping}
                  initialSortColumn="total_distinct_emails"
                  sortOrder="desc"
                  excelDownloadName={`doi_rates_email_domain_optin_${selectedOptInEmailForTable.opt_in_email_id}`}
                  onRowClick={handleEmailDomainRowClick}
                />
              </Paper>
            ) : (
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: '10px',
                  backgroundColor: colors.primary[400],
                }}
              >
                <Typography>
                  Bitte eine Opt-in Email aus der oberen Tabelle auswählen oder oben im Filter
                  wählen, um die Email Domain Raten anzuzeigen.
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: '10px', backgroundColor: colors.primary[400] }}>
              <Typography variant="h6" mb={2} fontWeight="bold">
                DOI-Rate im Zeitverlauf
                {selectedOptInEmailForTable && ` - ${selectedOptInEmailForTable.name}`}
                {selectedEmailDomain && ` - ${selectedEmailDomain}`}
              </Typography>
              {selectedOptInEmailForTable ? (
                selectedEmailDomain ? (
                  <DoiRateOverTimeChart
                    optInEmailId={selectedOptInEmailForTable.opt_in_email_id}
                    emailDomain={selectedEmailDomain}
                    abTestGmail={abTestGmailFilter}
                  />
                ) : (
                  <DoiRateOverTimeChart
                    optInEmailId={selectedOptInEmailForTable.opt_in_email_id}
                    abTestGmail={abTestGmailFilter}
                  />
                )
              ) : (
                <Box p={3} textAlign="center">
                  <Typography>
                    Bitte wählen Sie eine Opt-in Email aus der Tabelle oder Filter, um den DOI-Rate
                    Zeitverlauf anzuzeigen.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
          {selectedOptInEmailForTable && selectedEmailDomain && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, borderRadius: '10px', backgroundColor: colors.primary[400] }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight="bold">
                    Email Domain: {selectedEmailDomain}
                  </Typography>
                  <Box
                    sx={{
                      cursor: 'pointer',
                      color: colors.grey[100],
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => setSelectedEmailDomain(null)}
                  >
                    <Typography variant="body2">Zurücksetzen</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default DoiEvaluationOptinEmailsPage;
